import React from 'react';
import styles from './Dropdown.module.css';

type DropdownProps = {
    label?: string;
    options?: string[];
    value?: string | string[];
    onChange?: (value: string | string[]) => void;
    disabled?: boolean;
    required?: boolean;
    feedback?: 'success' | 'error' | 'warning';
    feedbackMessage?: string;
    isLoading?: boolean;
    multiple?: boolean;
    searchable?: boolean;
    defaultMessage?: string;
    variant?: 'primary' | 'secondary' | '';
    withBorder?: boolean;
    className?: string;
    name?: string;
    /**
     * Optional display-label transform (AB#2066 P1-A). The option string stays the
     * value (so callers' filter/sentinel logic is untouched); only the rendered
     * text is mapped. Defaults to identity — existing callers are unaffected.
     */
    getOptionLabel?: (option: string) => string;
};

const Dropdown: React.FC<DropdownProps> = ({
    label,
    options = [],
    value = '',
    onChange = () => {},
    disabled = false,
    required = false,
    feedback,
    feedbackMessage,
    isLoading = false,
    defaultMessage = 'Select an option',
    variant = '',
    withBorder = false,
    className,
    name,
    getOptionLabel = (option) => option,
}) => {
    const feedbackClass = feedback ? styles[feedback] : '';

    const currentValue = Array.isArray(value) ? value[0] ?? '' : value;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onChange(e.target.value);
    };

    const wrapperClasses = [
        styles.selectWrapper,
        disabled ? styles.disabled : '',
        variant ? styles[variant] : '',
        variant ? styles.xstyle : '',
        withBorder ? styles.withBorder : '',
        feedbackClass,
        className || '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={styles.dropdownContainer}>
            {label && (
                <label className={styles.label}>
                    {label} {required && '*'}
                </label>
            )}
            <div className={wrapperClasses}>
                {isLoading ? (
                    <div className={styles.loadingState}>
                        <span className={styles.spinner} />
                    </div>
                ) : (
                    <select
                        className={styles.nativeSelect}
                        value={currentValue}
                        onChange={handleChange}
                        disabled={disabled}
                        required={required}
                        name={name}
                    >
                        {/* Show placeholder when no value is selected */}
                        {(!currentValue || !options.includes(currentValue)) && (
                            <option value="" disabled>
                                {defaultMessage}
                            </option>
                        )}
                        {options.map((option, index) => (
                            <option key={index} value={option}>
                                {getOptionLabel(option)}
                            </option>
                        ))}
                    </select>
                )}
                {!isLoading && (
                    <svg
                        className={styles.chevron}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M2.5 4.5L6 8L9.5 4.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </div>
            {feedbackMessage && (
                <div className={`${styles.feedbackMessage} ${feedbackClass}`}>
                    {feedbackMessage}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
